import {useEffect, useMemo, useState} from "react";

import "./App.css";

function resolveApiBaseUrl() {
  const configuredBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").trim();

  if (configuredBaseUrl.startsWith("http://") || configuredBaseUrl.startsWith("https://")) {
    return configuredBaseUrl;
  }

  if (configuredBaseUrl.startsWith("/")) {
    if (window.location.port && window.location.port !== "4000") {
      return `${window.location.protocol}//${window.location.hostname}:4000${configuredBaseUrl}`;
    }

    return configuredBaseUrl;
  }

  return `${window.location.protocol}//${window.location.hostname}:4000/api`;
}

const apiBaseUrl = resolveApiBaseUrl();

function normalizeBucketState(data) {
  if (Array.isArray(data)) {
    return {
      configured: data.filter(Boolean),
      available: data.filter(Boolean).map(name => ({name}))
    };
  }

  return {
    configured: Array.isArray(data?.configured) ? data.configured : [],
    available: Array.isArray(data?.available) ? data.available : []
  };
}

function normalizeWeatherPayload(data) {
  if (!data || typeof data !== "object") {
    return null;
  }

  const values = data.values && typeof data.values === "object" ? data.values : {};

  return {
    bucket: data.bucket || "",
    measurement: data.measurement || "weather",
    timestamp: data.timestamp || null,
    device: data.device || null,
    location: data.location || null,
    values: {
      temperature: typeof values.temperature === "number" ? values.temperature : null,
      humidity: typeof values.humidity === "number" ? values.humidity : null,
      pressure: typeof values.pressure === "number" ? values.pressure : null
    }
  };
}

function normalizeHistoryPayload(data) {
  if (!data || typeof data !== "object") {
    return [];
  }

  if (!Array.isArray(data.points)) {
    return [];
  }

  return data.points.map(point => ({
    timestamp: point?.timestamp || null,
    temperature: typeof point?.temperature === "number" ? point.temperature : null,
    humidity: typeof point?.humidity === "number" ? point.humidity : null,
    pressure: typeof point?.pressure === "number" ? point.pressure : null
  }));
}

function formatValue(value, unit) {
  if (typeof value !== "number") {
    return "Ei arvoa";
  }

  return `${value.toFixed(2)} ${unit}`;
}

function formatTimestamp(value) {
  if (!value) {
    return "Ei tiedossa";
  }

  return new Date(value).toLocaleString("fi-FI");
}

function toApiTimestamp(value) {
  if (!value) {
    return "";
  }

  return new Date(value).toISOString();
}

function createHistoryRange(hours) {
  const to = new Date();
  const from = new Date(to.getTime() - hours * 60 * 60 * 1000);

  return {
    from: from.toISOString(),
    to: to.toISOString()
  };
}

function formatChartValue(value, unit) {
  if (typeof value !== "number") {
    return "Ei arvoa";
  }

  return `${value.toFixed(1)} ${unit}`;
}

function formatChartTimestamp(value) {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleTimeString("fi-FI", {
    hour: "2-digit",
    minute: "2-digit"
  });
}

function buildChartPath(points) {
  return points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(" ");
}

function WeatherHistoryChart({title, unit, valueKey, colorClass, points}) {
  const width = 320;
  const height = 180;
  const padding = 16;

  const validPoints = points
    .filter(point => point.timestamp && typeof point[valueKey] === "number")
    .map(point => ({
      timestamp: point.timestamp,
      value: point[valueKey]
    }));

  if (validPoints.length === 0) {
    return (
      <div className="graph-placeholder">
        <div className="graph-header">
          <span className="graph-title">{title}</span>
          <strong className="graph-value">Ei dataa</strong>
        </div>
        <div className="graph-empty">Ei historiadataa viimeiseltä 24 tunnilta.</div>
      </div>
    );
  }

  const minValue = Math.min(...validPoints.map(point => point.value));
  const maxValue = Math.max(...validPoints.map(point => point.value));
  const range = maxValue - minValue || 1;
  const stepX = validPoints.length > 1 ? (width - padding * 2) / (validPoints.length - 1) : 0;

  const chartPoints = validPoints.map((point, index) => ({
    ...point,
    x: padding + index * stepX,
    y: height - padding - ((point.value - minValue) / range) * (height - padding * 2)
  }));

  const latestPoint = validPoints.at(-1);
  const firstPoint = validPoints[0];

  return (
    <div className="graph-placeholder">
      <div className="graph-header">
        <span className="graph-title">{title}</span>
        <strong className="graph-value">{formatChartValue(latestPoint?.value, unit)}</strong>
      </div>

      <svg className={`graph-svg ${colorClass}`} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title}>
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} className="graph-axis" />
        <path d={buildChartPath(chartPoints)} className="graph-line" />
      </svg>

      <div className="graph-footer">
        <span>{formatChartTimestamp(firstPoint?.timestamp)}</span>
        <span>{formatChartTimestamp(latestPoint?.timestamp)}</span>
      </div>
    </div>
  );
}

function App() {
  const [bucketState, setBucketState] = useState({configured: [], available: []});
  const [selectedBucket, setSelectedBucket] = useState("");
  const [bucketDraft, setBucketDraft] = useState("");
  const [latestWeather, setLatestWeather] = useState(null);
  const [weatherHistory, setWeatherHistory] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [searchTimestamp, setSearchTimestamp] = useState("");
  const [fieldSummary, setFieldSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [savingBucket, setSavingBucket] = useState(false);
  const [error, setError] = useState("");
  const [bucketMessage, setBucketMessage] = useState("");

  const hasBuckets = bucketState.configured.length > 0;

  const activeBucket = useMemo(() => {
    if (selectedBucket) {
      return selectedBucket;
    }

    return bucketState.configured[0] || "";
  }, [bucketState.configured, selectedBucket]);

  async function fetchJson(path, options) {
    const response = await fetch(`${apiBaseUrl}${path}`, options);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || "Pyyntö epäonnistui");
    }

    return data;
  }

  async function loadBuckets() {
    const data = normalizeBucketState(await fetchJson("/buckets"));
    setBucketState(data);

    if (!selectedBucket && data.configured.length > 0) {
      setSelectedBucket(data.configured[0]);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setLoading(true);
      setError("");

      try {
        const bucketData = normalizeBucketState(await fetchJson("/buckets"));
        if (cancelled) {
          return;
        }

        setBucketState(bucketData);
        setSelectedBucket(current => current || bucketData.configured[0] || "");
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function refreshBucket() {
      if (!activeBucket) {
        setLatestWeather(null);
        setFieldSummary([]);
        setWeatherHistory([]);
        return;
      }

      setError("");

      try {
        const historyRange = createHistoryRange(24);
        const [latest, fields, history] = await Promise.all([
          fetchJson(`/weather/latest?bucket=${encodeURIComponent(activeBucket)}`)
            .then(normalizeWeatherPayload)
            .catch(() => null),
          fetchJson(`/buckets/${encodeURIComponent(activeBucket)}/fields`).catch(() => ({measurements: []})),
          fetchJson(
            `/weather/history?bucket=${encodeURIComponent(activeBucket)}&from=${encodeURIComponent(historyRange.from)}&to=${encodeURIComponent(historyRange.to)}`
          )
            .then(normalizeHistoryPayload)
            .catch(() => [])
        ]);

        if (!cancelled) {
          setLatestWeather(latest);
          setFieldSummary(fields.measurements || []);
          setWeatherHistory(history);
          setSearchResult(null);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
        }
      }
    }

    refreshBucket();

    return () => {
      cancelled = true;
    };
  }, [activeBucket]);

  async function handleBucketSubmit(event) {
    event.preventDefault();
    if (!bucketDraft.trim()) {
      return;
    }

    setSavingBucket(true);
    setBucketMessage("");
    setError("");

    try {
      const nextBucket = bucketDraft.trim();
      await fetchJson("/buckets", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({name: nextBucket})
      });

      await loadBuckets();
      setSelectedBucket(nextBucket);
      setBucketMessage(`Bucket "${nextBucket}" lisättiin backendin luettaviin bucketteihin.`);
      setBucketDraft("");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingBucket(false);
    }
  }

  async function handleSearch(event) {
    event.preventDefault();
    if (!activeBucket || !searchTimestamp) {
      return;
    }

    setSearching(true);
    setError("");

    try {
      const result = await fetchJson(
        `/weather/search?bucket=${encodeURIComponent(activeBucket)}&at=${encodeURIComponent(toApiTimestamp(searchTimestamp))}`
      );
      setSearchResult(normalizeWeatherPayload(result));
    } catch (searchError) {
      setSearchResult(null);
      setError(searchError.message);
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="app-shell">
      <div className="app-backdrop" />
      <main className="dashboard">
        <section className="hero">
          <div>
            <p className="eyebrow">IoT Weather Platform</p>
            <h1>InfluxDB-säädashboard</h1>
            <p className="hero-copy">
              Hallitse luettavia bucketteja, tarkista niiden kentät ja hae viimeisin tai tietyn ajanhetken säätila.
            </p>
          </div>
          <div className="hero-status">
            <span className="status-label">Aktiivinen bucket</span>
            <strong>{activeBucket || "Ei valittua bucketia"}</strong>
          </div>
        </section>

        {error && <div className="notice error">{error}</div>}
        {bucketMessage && <div className="notice success">{bucketMessage}</div>}

        <section className="grid two-column">
          <article className="panel">
            <div className="panel-header">
              <h2>Bucketit</h2>
              <span>{bucketState.configured.length} valittavissa</span>
            </div>

            <label className="field-label" htmlFor="bucket-select">Luettava bucket</label>
            <select
              id="bucket-select"
              className="select-input"
              value={activeBucket}
              onChange={event => setSelectedBucket(event.target.value)}
              disabled={!hasBuckets}
            >
              {!hasBuckets && <option value="">Ei bucketteja</option>}
              {bucketState.configured.map(bucket => (
                <option key={bucket} value={bucket}>
                  {bucket}
                </option>
              ))}
            </select>

            <form className="bucket-form" onSubmit={handleBucketSubmit}>
              <label className="field-label" htmlFor="bucket-name">Lisää bucket backendille</label>
              <div className="bucket-form-row">
                <input
                  id="bucket-name"
                  className="text-input"
                  type="text"
                  placeholder="esim. weather-archive"
                  value={bucketDraft}
                  onChange={event => setBucketDraft(event.target.value)}
                />
                <button className="primary-button" type="submit" disabled={savingBucket}>
                  {savingBucket ? "Tallennetaan..." : "Lisää"}
                </button>
              </div>
            </form>

            <div className="bucket-list">
              <h3>InfluxDB:ssä näkyvät bucketit</h3>
              <div className="chip-wrap">
                {bucketState.available.map(bucket => (
                  <button
                    key={bucket.id || bucket.name}
                    type="button"
                    className={`chip ${bucket.name === activeBucket ? "active" : ""}`}
                    onClick={() => setSelectedBucket(bucket.name)}
                  >
                    {bucket.name}
                  </button>
                ))}
              </div>
            </div>
          </article>

          <article className="panel">
            <div className="panel-header">
              <h2>Kentät bucketissa</h2>
              <span>{activeBucket || "Valitse bucket"}</span>
            </div>

            {!activeBucket && <p className="muted">Valitse bucket nähdäksesi mittauskentät.</p>}
            {activeBucket && fieldSummary.length === 0 && <p className="muted">Bucketissa ei löytynyt kenttiä.</p>}

            <div className="measurement-list">
              {fieldSummary.map(entry => (
                <div key={entry.measurement} className="measurement-card">
                  <h3>{entry.measurement}</h3>
                  <div className="chip-wrap">
                    {entry.fields.map(field => (
                      <span key={field} className="chip static">{field}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section className="grid two-column">
          <article className="panel spotlight">
            <div className="panel-header">
              <h2>Viimeisin säätila</h2>
              <span>{latestWeather ? formatTimestamp(latestWeather.timestamp) : "Ei dataa"}</span>
            </div>

            {loading && <p className="muted">Ladataan viimeisintä säätilaa...</p>}
            {!loading && !latestWeather && <p className="muted">Valitusta bucketista ei löytynyt säädataa.</p>}

            {latestWeather && (
              <>
                <div className="weather-meta">
                  <span>Laite: {latestWeather.device || "tuntematon"}</span>
                  <span>Sijainti: {latestWeather.location || "ei asetettu"}</span>
                  <span>Bucket: {latestWeather.bucket}</span>
                </div>

                <div className="stat-grid">
                  <div className="stat-card">
                    <span>Lämpötila</span>
                    <strong>{formatValue(latestWeather.values.temperature, "°C")}</strong>
                  </div>
                  <div className="stat-card">
                    <span>Kosteus</span>
                    <strong>{formatValue(latestWeather.values.humidity, "%")}</strong>
                  </div>
                  <div className="stat-card">
                    <span>Paine</span>
                    <strong>{formatValue(latestWeather.values.pressure, "hPa")}</strong>
                  </div>
                </div>
              </>
            )}
          </article>

          <article className="panel">
            <div className="panel-header">
              <h2>Hae sää ajanhetkellä</h2>
              <span>Vain oikea aikamuoto sallittu</span>
            </div>

            <form className="search-form" onSubmit={handleSearch}>
              <label className="field-label" htmlFor="weather-at">Ajanhetki</label>
              <input
                id="weather-at"
                className="text-input"
                type="datetime-local"
                step="60"
                value={searchTimestamp}
                onChange={event => setSearchTimestamp(event.target.value)}
                required
              />
              <button className="primary-button" type="submit" disabled={!activeBucket || searching}>
                {searching ? "Haetaan..." : "Hae säätila"}
              </button>
            </form>

            {searchResult && (
              <div className="search-result">
                <h3>Lähin löytynyt säätila</h3>
                <p>{formatTimestamp(searchResult.timestamp)}</p>
                <div className="result-list">
                  <span>Lämpötila: {formatValue(searchResult.values.temperature, "°C")}</span>
                  <span>Kosteus: {formatValue(searchResult.values.humidity, "%")}</span>
                  <span>Paine: {formatValue(searchResult.values.pressure, "hPa")}</span>
                </div>
              </div>
            )}
          </article>
        </section>

        <section className="panel">
          <div className="panel-header">
            <h2>Säähistoria</h2>
            <span>Backend hakee datan InfluxDB:stä viimeiseltä 24 tunnilta</span>
          </div>

          <div className="graph-placeholder-grid">
            <WeatherHistoryChart
              title="Lämpötilagraafi"
              unit="°C"
              valueKey="temperature"
              colorClass="temperature-line"
              points={weatherHistory}
            />
            <WeatherHistoryChart
              title="Kosteusgraafi"
              unit="%"
              valueKey="humidity"
              colorClass="humidity-line"
              points={weatherHistory}
            />
            <WeatherHistoryChart
              title="Painegraafi"
              unit="hPa"
              valueKey="pressure"
              colorClass="pressure-line"
              points={weatherHistory}
            />
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
